import {
  Mesh,
  DoubleSide,
  MeshBasicMaterial,
  TextureLoader,
  Vector3,
  PlaneGeometry,
  SphereGeometry,
  FrontSide,
  Sprite,
  SpriteMaterial,
} from 'three';

import VideoJs from 'video.js';

import { VRObject3D } from './VRObject3D';
import { cornerHandleLocalFromVertex, vertexLocalFromCornerHandle } from './helpers/worldHelpers';
import { dragTexture, meshTextures, myLocationTexture, videoPlayer as videoPlayerIconTexture, emptyTexture, INDICATOR_HIT_SIZE } from './helpers/texturesAndGeometries';
import { ICON } from "../Icons/Icons";
import { ComponentRegistry } from '../../constants/componentRegistry.js';

const videoModalWrapper = (uuid) => `
<div class="modal video-player-modal" id="video-player-modal-${uuid}">
  <div class="modal-background"></div>
  <div class="modal-content video-player-modal__content">
    <div class="video-window">
      <div class="video-window__header">
        <div class="video-window__title"></div>
        <button class="video-window__close-btn" aria-label="Close">
          ${ICON.CLOSE}
        </button>
      </div>
      <div class="video-window__body">
        <div class="video-aspect-ratio-placeholder" id="video-placeholder-${uuid}"></div>
        <div class="video-loading-overlay" id="video-loading-${uuid}">
          <div class="video-loading-overlay__content">
            <div class="video-loading-overlay__spinner"></div>
            <div class="video-loading-overlay__text">Loading video...</div>
            <div class="video-loading-overlay__progress">
              <div class="video-loading-overlay__progress-bar" id="video-progress-bar-${uuid}"></div>
            </div>
            <div class="video-loading-overlay__percentage" id="video-progress-text-${uuid}">0%</div>
          </div>
        </div>
        <video id="video-player-${uuid}" class="video-js vjs-big-play-centered" controls preload="auto">
          <p class="vjs-no-js">
            To view this video please enable JavaScript, and consider upgrading to a web browser that supports HTML5 video
          </p>
        </video>
      </div>
    </div>
  </div>
</div>
`;

class VRVideoPlayer extends VRObject3D {

  constructor(containerName, camera, texture, x, y, z, model) {
    super(containerName);
    this.objConfig.dragOnlyWhenSelected = false; // Will be set based on useIcon
    this.objConfig.isResizable = true;
    this.objConfig.isRotatable = false;
    this.objConfig.cubeSize = 20
    this.objConfig.offset = {
      topLeft: { x: 2, y: -3.5 },
      topRight: { x: -2, y: -3.5 },
      bottomRight: { x: -2, y: 3.5 },
      bottomLeft: { x: 2, y: 3.5 }
    }
    this.limits = {
      min: 200,   // New min value
      max: 900,   // New max value
      distance: 500 // New distance value
    };
    this.perspectivePosition = {
      "topLeft": new Vector3(20, -20, 0),
      "topRight": new Vector3(20, 20, 0),
      "bottomRight": new Vector3(20, -20, 0),
      "bottomLeft": new Vector3(-20, -20, 0),
    };
    this.originalPerspectivePosition = {
      "topLeft": new Vector3(20, -20, 0),
      "topRight": new Vector3(20, 20, 0),
      "bottomRight": new Vector3(20, -20, 0),
      "bottomLeft": new Vector3(-20, -20, 0),
    };

    this.dragCubeCorners = {};
    this.dragSprites = {};
    this.cornerPoints = {};
    this.centerHandle = null;
    this.centerHandleSprite = null;

    this.model = model;
    this.camera = camera;
    this.useIcon = true; // Default to using icon
    this.customIcon = false;
    this.videoPlayerImage = null; // Will store the uploaded image URL
    this.material = new MeshBasicMaterial({ side: DoubleSide, transparent: true, map: texture || videoPlayerIconTexture });
    this.geometry = new PlaneGeometry(64, 64);
    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.type = 'videoPlayer';
    this.mesh.name = 'videoPlayer';
    this.name = 'videoPlayer';
    this.type = 'videoPlayer';
    // this.mesh.rotation.z = -Math.PI / 2; // Rotate -90 degrees around the Z-axis
    this.borderMesh = new Mesh(this.geometry, new MeshBasicMaterial({ side: FrontSide, transparent: true, color: "white", opacity: 0 }));
    this.borderMesh.name = "videoPlayerBorder"
    if (!this.editMode) {
      this.mesh.add(this.borderMesh);
    }

    this.loader = new TextureLoader();

    // INIT
    this.camera !== null ? this._setInitialPosRot() : this.setPosition(x, y, z);

    // Scale down the mesh to match imageGallery size
    this.mesh.scale.set(1, 1, 1);

    this.add(this.mesh);

    this.mesh.groupDetails = { title: $trans('info-element'), icon: 'info', editTitle: $trans('edit-video-player-element-text'), dataLang: 'edit-video-player-element-text' }

    this.editControls();

    // Set initial drag behavior based on useIcon (default is true)
    this.updateDragBehavior(true);

    // Initialize video modal
    this.uuid = new aptis.UUIDGenerator().generate();
    this.videoUrl = null;
    this.videoTitle = null;
    this.videoPlayer = null;
    this._playbackActive = false;
    this._modalVideoLifecycleBound = false;

    // Create modal wrapper
    this.modalWrapper = $(videoModalWrapper(this.uuid));
    $("body").append(this.modalWrapper);

    // Setup close button
    this.closeBtn = this.modalWrapper.find(".video-window__close-btn");
    this.closeBtn.on("click", () => this.closeModal());

    // Close on background click
    this.modalWrapper.find(".modal-background").on("click", () => this.closeModal());

  }

  getLimits() {
    const min = Number(this.limits?.min) || 200;
    const max = Number(this.limits?.max) || 900;
    const configuredDistance = Number(this.dbConfig?.distance);
    const runtimeDistance = Number(this.limits?.distance);
    const distance = Number.isFinite(configuredDistance) && configuredDistance > 0
      ? configuredDistance
      : (Number.isFinite(runtimeDistance) && runtimeDistance > 0 ? runtimeDistance : 500);

    return { min, max, distance };
  }

  setDistance(val) {
    const n = Number(val);
    if (!Number.isFinite(n) || n <= 0) return;
    const { min, max } = this.getLimits();
    const clamped = Math.min(max, Math.max(min, n));
    this.limits.distance = clamped;
    this.dbConfig = { ...this.dbConfig, distance: clamped };
  }

  /**
   * Calculate dimensions based on aspect ratio
   * Uses 20% of screen size for the longer dimension
   * @param {number} aspectRatio - Image aspect ratio
   * @returns {{w: number, h: number}} Width and height
   */
  calculateDimensionsFromAspectRatio(aspectRatio) {
    // Get screen dimensions (20% of screen size)
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const targetSize = Math.min(screenWidth, screenHeight) * 0.2;

    let w = 1, h = 1;
    if (aspectRatio < 1) { // Portrait (height > width)
      h = targetSize;
      w = targetSize * aspectRatio;
    } else { // Landscape or square (width >= height)
      w = targetSize;
      h = targetSize / aspectRatio;
    }
    return { w, h };
  }

  /**
   * Update mesh geometry with new dimensions
   * @param {number} width - New width
   * @param {number} height - New height
   */
  updateMeshGeometry(width, height) {
    // Dispose old geometry
    if (this.mesh.geometry) {
      this.mesh.geometry.dispose();
    }

    // Create new geometry
    this.mesh.geometry = new PlaneGeometry(width, height);
    this.mesh.geometry.computeBoundingBox();
    this.mesh.geometry.computeBoundingSphere();
    this.mesh.updateMatrixWorld(true);
  }

  /**
   * Reset perspective corner positions based on dimensions
   * @param {number} width - Mesh width
   * @param {number} height - Mesh height
   */
  resetPerspectivePositions(width, height) {
    const halfW = width / 2;
    const halfH = height / 2;
    const corners = {
      "topLeft": new Vector3(-halfW, halfH, 0),
      "topRight": new Vector3(halfW, halfH, 0),
      "bottomRight": new Vector3(halfW, -halfH, 0),
      "bottomLeft": new Vector3(-halfW, -halfH, 0)
    };

    Object.entries(corners).forEach(([name, vector]) => {
      this.perspectivePosition[name] = vector;
      this.dbConfig[name] = vector.clone();
      this.dbConfig.original[name] = vector.clone();
      this.originalPerspectivePosition[name] = vector.clone();
    });
  }

  resetPerspective = () => {
    this.removeDragPoints();

    // Check if there's a valid texture with image
    const texture = this.mesh.material.map;
    if (this.dbConfig?.images?.length) {
      if (texture && texture.image && texture.image.width && texture.image.height) {
        // Has image - reset to image's original dimensions
        let aspectRatio = texture.image.width / texture.image.height;
        if (aspectRatio === undefined || aspectRatio === 0) {
          aspectRatio = 16 / 9;
        }

        const { w, h } = this.calculateDimensionsFromAspectRatio(aspectRatio);
        this.updateMeshGeometry(w, h);
        this.resetValuesWithNewTexture(texture);
      }
    } else {
      // No image - reset to default size (64x64)
      this.updateMeshGeometry(64, 64);
      this.resetPerspectivePositions(64, 64);
    }

    this.applyPerspectiveDragAndDrop();
    this.addDragPoints();
  }

  setDbConfig = (conf) => {
    super.setDbConfig(conf);
    const savedSize = Number(this.dbConfig?.size);
    if (Number.isFinite(savedSize) && savedSize > 0) {
      this.mesh.scale.set(savedSize, savedSize, savedSize);
    }

    Object.entries(this.perspectivePosition).forEach(([name]) => {
      if (this.perspectivePosition[name]) {
        const prop = this.dbConfig[name];
        if (prop) {
          this.perspectivePosition[name] = new Vector3(prop.x, prop.y, prop.z);
        }
      }
    });

    // Restore useIcon state and apply texture
    if (conf.useIcon !== undefined) {
      this.useIcon = conf.useIcon;
      this.customIcon = this.useIcon === false;
      if (conf.customImage !== undefined) {
        const rawCustomImage = conf.customImage || null;
        this.videoPlayerImage = rawCustomImage && !String(rawCustomImage).includes('edit_play_video.png')
          ? rawCustomImage
          : null;
      }

      // Set drag behavior based on useIcon
      this.objConfig.dragOnlyWhenSelected = !this.useIcon;

      // Add drag points only if not using icon; remove them in icon/indicator mode
      if (this.model.editMode) {
        if (this.useIcon) {
          this.removeDragPoints();
        } else {
          this.addDragPoints();
        }
      }

      this.updateMeshTexture();
    } else {
      // Default behavior if useIcon not in config
      if (this.model.editMode && !this.useIcon) {
        this.addDragPoints();
      }
    }

    // Restore video URL, title, and dimensions
    if (conf.videoUrl) {
      this.videoUrl = conf.videoUrl;
      this.videoTitle = conf.videoTitle || null;
      this.videoWidth = conf.videoWidth || null;
      this.videoHeight = conf.videoHeight || null;
      this.videoRatio = conf.videoRatio || null;
    }
  }

  setUseIcon = (useIcon) => {
    this.useIcon = useIcon;
    this.customIcon = this.useIcon === false;
    this.updateDragBehavior();
    this.updateMeshTexture();
  }

  /** True when the player should render as the 2D play indicator (no custom mesh image). */
  isIndicatorMode = () => {
    return !!this.useIcon || !this.customIcon;
  }

  updateDragBehavior = (isInitializing = false) => {
    if (this.useIcon) {
      // Icon mode: can drag freely without selection, no corners/centerHandle
      this.objConfig.dragOnlyWhenSelected = false;
      if (this.model.editMode) {
        if (!isInitializing) {
          // Reset to indicator-sized hit target when switching to icon mode
          this.updateMeshGeometry(INDICATOR_HIT_SIZE, INDICATOR_HIT_SIZE);
          this.resetPerspectivePositions(INDICATOR_HIT_SIZE, INDICATOR_HIT_SIZE);
          this.applyPerspectiveDragAndDrop();

          this.removeDragPoints();
        }

        // If currently selected, hide drag handles immediately
        if (this.isSelected) {
          this.hideDragHandles();
        }
      }
    } else {
      // Custom image mode: must select to drag, show corners/centerHandle
      this.objConfig.dragOnlyWhenSelected = true;
      if (this.model.editMode) {
        if (!isInitializing) {
          this.addDragPoints();
        }
        // If currently selected, show drag handles immediately
        if (this.isSelected) {
          this.showDragHandles();
        }
      }
    }
  }

  updateMeshTexture = (imageLoadCallback = null, resetPerspective = false) => {
    if (this.useIcon) {
      // Edit mode: small invisible hit target + 2D indicator. View mode: mesh is hidden by indicator flow.
      if (this.model?.editMode) {
        this.material.map = emptyTexture;
        this.updateMeshGeometry(INDICATOR_HIT_SIZE, INDICATOR_HIT_SIZE);
        this.resetPerspectivePositions(INDICATOR_HIT_SIZE, INDICATOR_HIT_SIZE);
      } else {
        this.material.map = videoPlayerIconTexture;
      }
      this.material.needsUpdate = true;
      this.applyPerspectiveDragAndDrop();
      imageLoadCallback && imageLoadCallback();
    } else {
      // Use the custom image if available
      if (this.videoPlayerImage) {
        this.loadCustomImage(this.videoPlayerImage, imageLoadCallback, resetPerspective);
      } else {
        // Fallback when custom mode has no image yet
        if (this.model?.editMode) {
          this.material.map = emptyTexture;
          this.updateMeshGeometry(INDICATOR_HIT_SIZE, INDICATOR_HIT_SIZE);
          this.resetPerspectivePositions(INDICATOR_HIT_SIZE, INDICATOR_HIT_SIZE);
        } else {
          this.material.map = videoPlayerIconTexture;
        }
        this.material.needsUpdate = true;
        this.applyPerspectiveDragAndDrop();
        imageLoadCallback && imageLoadCallback();
      }
    }
  }

  /**
   * Load custom image for video player
   * Used when uploading new image or reloading page with saved custom image
   */
  loadCustomImage(url, callback = null, resetPerspective = false) {
    // Check if texture is already cached
    if (meshTextures[url]) {
      this.material.map = meshTextures[url];
      this.material.needsUpdate = true;
      this.applyPerspectiveDragAndDrop();
      callback && callback();
      return;
    }

    // Load new texture
    this.loader.load(url, (texture) => {
      // Calculate aspect ratio and dimensions
      let aspectRatio = texture.image.width / texture.image.height;
      if (aspectRatio === undefined || aspectRatio === 0) {
        aspectRatio = 16 / 9; // Default aspect ratio
      }

      const { w, h } = this.calculateDimensionsFromAspectRatio(aspectRatio);

      // Cache texture
      meshTextures[url] = texture;

      // Update material
      this.material.map = texture;
      this.material.needsUpdate = true;

      // Update geometry to match image dimensions
      this.updateMeshGeometry(w, h);

      // Only reset perspective positions if NOT loading from saved config
      // When loading from config, we want to preserve the saved corner positions
      if (resetPerspective) {
        // Reset perspective positions based on new dimensions (new upload)
        this.resetPerspectivePositions(w, h);
      } else {
        // Loading from config - just update originalPerspectivePosition for reference
        const halfW = w / 2, halfH = h / 2;
        const corners = {
          "topLeft": new Vector3(-halfW, halfH, 0),
          "topRight": new Vector3(halfW, halfH, 0),
          "bottomRight": new Vector3(halfW, -halfH, 0),
          "bottomLeft": new Vector3(-halfW, -halfH, 0)
        };
        Object.entries(corners).forEach(([name, vector]) => {
          this.originalPerspectivePosition[name] = vector.clone();
        });
      }

      if (callback) { callback(); }

      this.removeDragPoints();
      this.applyPerspectiveDragAndDrop();
      this.model.editMode && this.addDragPoints();
    });
  }


  resetValuesWithNewTexture(texture) {
    let aspectRatio = texture.image.width / texture.image.height;
    if (aspectRatio === undefined || aspectRatio === 0) {
      aspectRatio = 16 / 9;
    }

    const { w, h } = this.calculateDimensionsFromAspectRatio(aspectRatio);
    this.resetPerspectivePositions(w, h);
  }

  applyPerspectiveDragAndDrop() {
    const points = this.perspectivePosition;
    const positionAttribute = this.mesh.geometry.attributes.position;
    const uvAttribute = this.mesh.geometry.attributes.uv;

    // Mapping the corners to the indices based on their order in the mesh's vertices
    const cornerMappings = {
      topLeft: 0,
      topRight: 1,
      bottomRight: 3,
      bottomLeft: 2
    };

    // Loop through each corner and update its position
    Object.entries(cornerMappings).forEach(([corner, index]) => {
      if (points[corner]) {
        positionAttribute.setXY(index, points[corner].x, points[corner].y);

        if (this.dragCubeCorners && this.dragCubeCorners[corner]) {
          const handle = cornerHandleLocalFromVertex(points[corner], this.objConfig, corner);
          this.dragCubeCorners[corner].position.set(handle.x, handle.y, handle.z);
        }
      }
    });

    // Update UVs to standard mapping
    uvAttribute.setXY(cornerMappings['topLeft'], 0, 1); // UV for topLeft
    uvAttribute.setXY(cornerMappings['topRight'], 1, 1); // UV for topRight
    uvAttribute.setXY(cornerMappings['bottomRight'], 1, 0); // UV for bottomRight
    uvAttribute.setXY(cornerMappings['bottomLeft'], 0, 0); // UV for bottomLeft

    positionAttribute.needsUpdate = true;
    uvAttribute.needsUpdate = true; // Notify Three.js that the UVs have changed

    // Update bounding box after deforming geometry
    this.mesh.geometry.computeBoundingBox();
    this.mesh.geometry.computeBoundingSphere();

    this.applyPerspectiveDragAndDropBorder()
  }
  applyPerspectiveDragAndDropBorder() {
    const points = this.perspectivePosition;
    const positionAttribute = this.borderMesh.geometry.attributes.position;
    const uvAttribute = this.borderMesh.geometry.attributes.uv;

    // Mapping the corners to the indices based on their order in the mesh's vertices
    const cornerMappings = {
      topLeft: 0,
      topRight: 1,
      bottomRight: 3,
      bottomLeft: 2
    };

    // Loop through each corner and update its position
    Object.entries(cornerMappings).forEach(([corner, index]) => {
      if (points[corner]) {
        positionAttribute.setXY(index, points[corner].x, points[corner].y);
      }
    });

    // Update UVs to standard mapping
    uvAttribute.setXY(cornerMappings['topLeft'], 0, 1); // UV for topLeft
    uvAttribute.setXY(cornerMappings['topRight'], 1, 1); // UV for topRight
    uvAttribute.setXY(cornerMappings['bottomRight'], 1, 0); // UV for bottomRight
    uvAttribute.setXY(cornerMappings['bottomLeft'], 0, 0); // UV for bottomLeft

    positionAttribute.needsUpdate = true;
    uvAttribute.needsUpdate = true; // Notify Three.js that the UVs have changed

  }

  updateMeshCorner(cornerName, newPosition) {
    const positionAttribute = this.mesh.geometry.attributes.position;
    const cornerMappings = {
      topLeft: 0,
      topRight: 1,
      bottomRight: 3,
      bottomLeft: 2
    };

    const vertex = vertexLocalFromCornerHandle(newPosition, this.objConfig, cornerName);
    const vertexPosition = new Vector3(vertex.x, vertex.y, vertex.z);

    this.perspectivePosition[cornerName] = vertexPosition;
    this.dbConfig[cornerName] = vertexPosition.clone();

    const vertexIndex = cornerMappings[cornerName];
    if (vertexIndex !== undefined) {
      positionAttribute.setXYZ(vertexIndex, vertexPosition.x, vertexPosition.y, vertexPosition.z);
      positionAttribute.needsUpdate = true;
      this.mesh.geometry.computeBoundingBox();
      this.mesh.geometry.computeBoundingSphere();
    }
  }
  onSelect() {
    // Show drag handles when object is selected
    this.showDragHandles();
    // Hide border in edit mode
    if (this.model.editMode && this.borderMesh) {
      this.borderMesh.visible = false;
    }
    super.onSelect();
  }

  onDeselect() {
    // Hide drag handles when object is deselected
    this.hideDragHandles();

    // Cleanup: Move settings back to modal when deselected
    const settings = $("#create-videoPlayer-settings");
    if (settings.closest(".object-settings-content").length) {
      $(".video-player-modal .modal-data").append(settings);
    }

    super.onDeselect();
  }

  showDragHandles() {
    // Don't show drag handles when using icon mode
    if (this.useIcon) {
      return;
    }

    Object.values(this.dragSprites).forEach(sprite => {
      if (sprite) sprite.visible = true;
    });
    Object.values(this.dragCubeCorners).forEach(cube => {
      if (cube) cube.visible = true;
    });
    if (this.centerHandle) this.centerHandle.visible = true;
    if (this.centerHandleSprite) this.centerHandleSprite.visible = true;
  }

  hideDragHandles() {
    Object.values(this.dragSprites).forEach(sprite => {
      if (sprite) sprite.visible = false;
    });
    Object.values(this.dragCubeCorners).forEach(cube => {
      if (cube) cube.visible = false;
    });
    if (this.centerHandle) this.centerHandle.visible = false;
    if (this.centerHandleSprite) this.centerHandleSprite.visible = false;
  }

  addDragPoints() {
    if (this.dragCubeCorners["topLeft"]) {
      return;
    }
    const sphereRadius = this.objConfig.cubeSize / 2;
    const hitRadius = sphereRadius * (this.objConfig.cornerHitAreaScale || 1);
    const cubeGeometry = new SphereGeometry(hitRadius, 8, 8);
    const cubeMaterial = new MeshBasicMaterial({
      color: 0x6B9D11,
      opacity: 0,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      renderOrder: 999,
    });

    // Check if the mesh's geometry's positions are accessible
    if (!this.mesh.geometry.attributes.position) {
      console.error("Mesh geometry does not have position attributes.");
      return;
    }

    const positionAttribute = this.mesh.geometry.attributes.position;
    if (positionAttribute.count < 4) {
      console.error("Mesh does not have enough vertices to define four corners.");
      return;
    }

    // Define corner positions by the first four vertices in the geometry
    // This assumes the vertices are laid out as:
    // 0 - topLeft, 1 - topRight, 3 - bottomRight, 2 - bottomLeft (depending on the draw order)
    const cornerNames = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
    const cornerIndices = [0, 1, 3, 2];  // Adjust these indices according to your specific geometry

    this.dragCubeCorners = {};  // Store references to the corner cubes

    cornerIndices.forEach((index, i) => {
      const offset = this.objConfig.offset[cornerNames[i]]

      // Access vertex positions
      const x = positionAttribute.getX(index) + (offset?.x || 0);
      const y = positionAttribute.getY(index) + (offset?.y || 0);
      const z = positionAttribute.getZ(index);

      // Create cube and position it at the vertex
      const cube = new Mesh(cubeGeometry, cubeMaterial);

      cube.name = cornerNames[i];  // Name the cube based on its corner
      cube.position.set(x, y + this.objConfig.cubeSize / 2, z);

      // Create spherical background sprite for better raycasting from all angles
      const spriteRadius = this.objConfig.cubeSize / 2 * 1.1;
      const spriteGeometry = new SphereGeometry(spriteRadius, 16, 16);
      const spriteMaterial = new MeshBasicMaterial({
        color: 0x6B9D11, // Green color
        transparent: true,
        opacity: 0.5,
        depthTest: false
      });

      const sprite = new Mesh(spriteGeometry, spriteMaterial);
      sprite.name = `sprite_${cornerNames[i]}`;
      sprite.position.set(0, -this.objConfig.cubeSize / 2, 0);
      sprite.raycast = () => { };

      // Create icon sprite on top of circle
      const spriteIconMaterial = new SpriteMaterial({
        map: myLocationTexture,
        depthTest: false,
        transparent: true
      });
      const spriteIcon = new Sprite(spriteIconMaterial);
      spriteIcon.name = `spriteIcon_${cornerNames[i]}`;
      spriteIcon.position.set(0, -this.objConfig.cubeSize / 2, 0.1);
      spriteIcon.scale.set(this.objConfig.cubeSize / 2, this.objConfig.cubeSize / 2, this.objConfig.cubeSize / 2);
      spriteIcon.raycast = () => { };

      cube.add(sprite);
      cube.add(spriteIcon);

      // Store the sprite reference for future access
      this.dragSprites[sprite.name] = sprite;

      // Add the cube to the mesh so it inherits transformations
      this.mesh.add(cube);

      // Store the cube reference for future access
      this.dragCubeCorners[cube.name] = cube;
    });

    // Create center handle (sphere)
    const sphereGeometry = new SphereGeometry(hitRadius, 8, 8);
    const sphereMaterial = new MeshBasicMaterial({
      color: 0x6B9D11, // green color
      opacity: 0,
      transparent: true
    });

    this.centerHandle = new Mesh(sphereGeometry, sphereMaterial);
    this.centerHandle.name = 'centerHandle';
    this.centerHandle.position.set(0, 0, 0); // Center of the mesh
    this.centerHandle.raycast = () => { };

    // Create spherical background for center handle for better raycasting
    const centerSpriteRadius = sphereRadius * 1.1;
    const centerSpriteGeometry = new SphereGeometry(centerSpriteRadius, 16, 16);
    const centerSpriteMaterial = new MeshBasicMaterial({
      color: 0x6B9D11, // green color
      transparent: true,
      opacity: 0.5,
      depthTest: false
    });
    this.centerHandleSprite = new Mesh(centerSpriteGeometry, centerSpriteMaterial);
    this.centerHandleSprite.name = 'sprite_centerHandle';
    this.centerHandleSprite.position.set(0, 0, 0);
    this.centerHandleSprite.raycast = () => { };

    // Create icon sprite on top of circle
    const centerSpriteIconMaterial = new SpriteMaterial({
      map: dragTexture,
      depthTest: false,
      transparent: true
    });
    this.centerHandleSpriteIcon = new Sprite(centerSpriteIconMaterial);
    this.centerHandleSpriteIcon.name = 'spriteIcon_centerHandle';
    this.centerHandleSpriteIcon.position.set(0, 0, 0.1); // Slightly in front
    this.centerHandleSpriteIcon.scale.set(sphereRadius, sphereRadius, sphereRadius);
    this.centerHandleSpriteIcon.raycast = () => { };

    this.centerHandle.add(this.centerHandleSprite);
    this.centerHandle.add(this.centerHandleSpriteIcon);
    this.mesh.add(this.centerHandle);

    // Initially hide all handles
    this.hideDragHandles();
  }
  animateBorder() {
    this.borderMesh.material.opacity = .25;
  }
  removeBorder() {
    this.borderMesh.material.opacity = 0;
  }
  removeDragPoints() {
    Object.entries(this.dragSprites).forEach(([name, sprite]) => {
      if (this.dragSprites[name]) {
        this.dragSprites[name].parent?.remove(this.dragSprites[name]);
        this.dragSprites[name] = null;
      }
    });
    Object.entries(this.dragCubeCorners).forEach(([name, corner]) => {
      this.mesh.remove(this.dragCubeCorners[name]);
      this.dragCubeCorners[name] = null;
    });

    // Remove center handle
    if (this.centerHandle) {
      this.mesh.remove(this.centerHandle);
      this.centerHandle = null;
    }
    if (this.centerHandleSprite) {
      this.centerHandleSprite = null;
    }
    if (this.centerHandleSpriteIcon) {
      this.centerHandleSpriteIcon = null;
    }
  }

  _getVideoEventSourceId() {
    return this.dbConfig?.id ?? this.getId?.() ?? this.id ?? this.unique_id;
  }

  _fireVideoEvent(eventType) {
    if (!eventType || this.model?.editMode) return;
    const sourceId = this._getVideoEventSourceId();
    if (sourceId == null) return;
    this.model?.view?._executeEventActions?.(sourceId, eventType);
  }

  _onVideoPlaybackStarted() {
    if (this.model?.editMode) return;
    if (!this._playbackActive) {
      this._playbackActive = true;
      this._fireVideoEvent('videoStartedPlaying');
    }
  }

  _onVideoPlaybackStopped() {
    if (!this._playbackActive) return;
    this._playbackActive = false;
    this._fireVideoEvent('stoppedPlaying');
  }

  _ensureModalVideoLifecycleListeners() {
    if (!this.videoPlayer || this._modalVideoLifecycleBound) return;
    this._modalVideoLifecycleBound = true;
    this.videoPlayer.on('playing', () => {
      this._onVideoPlaybackStarted();
    });
    this.videoPlayer.on('ended', () => {
      this._onVideoPlaybackStopped();
    });
  }

  _pauseAndRewind() {
    if (!this.videoPlayer) return;
    this.videoPlayer.pause();
    try {
      this.videoPlayer.currentTime(0);
    } catch (_) { }
  }

  stopPlaying() {
    this._pauseAndRewind();
    this._onVideoPlaybackStopped();
  }

  closeVideo() {
    this.stopPlaying();
    this.modalWrapper?.removeClass("is-active");
  }

  /**
   * Open video modal and initialize video player
   */
  openModal() {
    if (!this.videoUrl) {
      this.model.alert('error', $trans('no-video-uploaded-yet'));
      return;
    }

    const isFistLoad = !this.videoPlayer;
    if (!this.videoPlayer) {
      // Initialize Video.js
      this.videoPlayer = VideoJs(document.getElementById(`video-player-${this.uuid}`), {
        controls: true,
        autoplay: false,
        preload: 'auto',
        fluid: true,
        responsive: true,
        fill: false,
        controlBar: {
          playToggle: true,
          volumePanel: true,
          currentTimeDisplay: true,
          timeDivider: true,
          durationDisplay: true,
          progressControl: true,
          remainingTimeDisplay: false,
          pictureInPictureToggle: false,
          fullscreenToggle: true
        }
      });
    }

    this._ensureModalVideoLifecycleListeners();

    const $el = {
      placeholder: $(`#video-placeholder-${this.uuid}`),
      loadingOverlay: $(`#video-loading-${this.uuid}`),
      progressBar: $(`#video-progress-bar-${this.uuid}`),
      progressText: $(`#video-progress-text-${this.uuid}`),
      videoPlayer: $(`#video-player-${this.uuid}`),
      video: this.modalWrapper.find("video"),
      title: this.modalWrapper.find(".video-window__title"),
      modal: this.modalWrapper,
    }

    // Set title
    const displayTitle = this.videoTitle || this.getFileNameFromUrl(this.videoUrl);
    $el.title.text(displayTitle);

    // Show modal
    $el.modal.addClass("is-active");

    const showVideo = () => {
      $el.loadingOverlay.hide();
      $el.placeholder.hide();
      $el.videoPlayer.show();
    }

    const showLoading = () => {
      $el.placeholder.show();
      $el.loadingOverlay.css('display', 'flex');
      $el.progressBar.css('width', '0%');
      $el.progressText.text('0%');
    }

    // On first load, set event listeners
    if (isFistLoad) {
      // Track loading progress
      this.videoPlayer.on('progress', () => {
        const buffered = this.videoPlayer.buffered();
        if (buffered.length > 0) {
          const duration = this.videoPlayer.duration();
          if (duration > 0) {
            const bufferedEnd = buffered.end(buffered.length - 1);
            const percentage = Math.round((bufferedEnd / duration) * 100);

            $el.progressBar.css('width', percentage + '%');
            $el.progressText.text(percentage + '%');
          }
        }
      });

      this.videoPlayer.on('playing', () => {
        showVideo();
      });

      this.videoPlayer.ready(() => {
        this.videoPlayer.play();
      });
    }

    const currentUrl = $el.video.attr('src');
    if (!currentUrl || currentUrl !== this.videoUrl) {
      if (this.videoRatio) {
        const paddingBottom = (1 / parseFloat(this.videoRatio)) * 100;
        $el.placeholder.css('padding-bottom', paddingBottom + '%');
        $el.placeholder.show();
      } else {
        // Default 16:9 if no ratio available
        $el.placeholder.css('padding-bottom', '56.25%');
        $el.placeholder.show();
      }

      // Hide video element initially
      $el.videoPlayer.hide();
    }

    // Show loading overlay
    showLoading();

    // Set video source and autoplay
    this.videoPlayer.src({
      src: this.videoUrl,
      type: this.getVideoType(this.videoUrl)
    });

    if (!isFistLoad) {
      this.videoPlayer.currentTime(0);
      this.videoPlayer.play();
      showVideo();
    }
  }

  /**
   * Close video modal and stop playback
   */
  closeModal() {
    this.closeVideo();
  }

  /**
   * Get video MIME type from URL
   */
  getVideoType(url) {
    const extension = url.split('.').pop().toLowerCase().split('?')[0];
    const types = {
      'mp4': 'video/mp4',
      'webm': 'video/webm',
      'ogg': 'video/ogg',
      'mov': 'video/quicktime',
      'avi': 'video/x-msvideo',
      'm3u8': 'application/x-mpegURL'
    };
    return types[extension] || 'video/mp4';
  }

  /**
   * Extract filename from URL
   */
  getFileNameFromUrl(url) {
    return url.split('/').pop().split('?')[0];
  }

  /**
   * Set video URL and title
   */
  setVideoUrl(url, title = null) {
    this.videoUrl = url;
    this.videoTitle = title;
  }

  /**
   * Cleanup when object is destroyed
   */
  dispose() {
    // Dispose Video.js player
    if (this.videoPlayer) {
      this.videoPlayer.dispose();
      this.videoPlayer = null;
    }

    // Remove modal from DOM
    if (this.modalWrapper) {
      this.modalWrapper.remove();
    }

    super.dispose && super.dispose();
  }

}

ComponentRegistry.register({
  type: 'videoPlayer',
  displayNameKey: 'add-video-player-text',
  getDisplayName: (obj) => {
    const title = obj?.videoTitle != null ? String(obj.videoTitle).trim() : '';
    return title || $trans('add-video-player-text') || 'Video player';
  },
  events: [
    { value: 'click', labelKey: 'event-click' },
    { value: 'videoStartedPlaying', labelKey: 'event-video-started-playing' },
    { value: 'stoppedPlaying', labelKey: 'event-stopped-playing' },
    { value: 'raycastEnter', labelKey: 'event-raycast-enter' },
    { value: 'raycastLeave', labelKey: 'event-raycast-leave' },
  ],
  actions: [
    { value: 'play', labelKey: 'action-play' },
    { value: 'pause', labelKey: 'action-pause' },
    { value: 'stopPlaying', labelKey: 'action-stop-playing' },
    { value: 'closeVideo', labelKey: 'action-close-video' },
    { value: 'closeElement', labelKey: 'action-close-element' },
  ],
});

export { VRVideoPlayer };
